import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'garminOAuthTransaction';

export function useGarminOAuth() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const queryClient = useQueryClient();
  const [isStarting, setIsStarting] = useState(false);

  const checkStatus = useCallback(async () => {
    const transactionId = localStorage.getItem(STORAGE_KEY);
    if (!transactionId || !isAuthenticated) return;

    const token = await getAccessTokenSilently();
    const response = await fetch(
      `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/integrations/garmin/oauth/${encodeURIComponent(transactionId)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (response.status === 404) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    if (!response.ok) return;
    const { status } = await response.json();
    if (status === 'complete' || status === 'failed') {
      localStorage.removeItem(STORAGE_KEY);
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
    }
  }, [getAccessTokenSilently, isAuthenticated, queryClient]);

  useEffect(() => {
    void checkStatus();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void checkStatus();
    };
    window.addEventListener('focus', checkStatus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', checkStatus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [checkStatus]);

  const connect = useCallback(
    async (returnTo: string) => {
      setIsStarting(true);
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/integrations/garmin/oauth`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ returnTo }),
          },
        );
        if (!response.ok)
          throw new Error('Unable to start Garmin authorization');
        const { transactionId, authorizationUrl } = await response.json();
        localStorage.setItem(STORAGE_KEY, transactionId);
        window.location.assign(authorizationUrl);
      } finally {
        setIsStarting(false);
      }
    },
    [getAccessTokenSilently],
  );

  return { connect, isStarting };
}
